// AI秘書の状態管理フック。死活ポーリング（睡眠↔覚醒）、チャット送受信、
// セッション（会話履歴の単位）の管理を担う。
//
// 履歴は Firestore に保存する（ログイン本人のみ読み書き可）。
// 画面の応答性を保つため、state 更新は同期・保存は裏で非同期に行う。
import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '@modules/auth';
import { HEALTH_POLL_MS } from '../config';
import { checkHealth } from '../services/gateway.service';
import { runAgent } from '../services/agent.service';
import {
  loadCurrentId,
  loadSessions,
  removeSession,
  saveCurrentId,
  saveSession,
} from '../services/history.service';
import type { ChatMessage, ChatSession, SecretaryStatus } from '../types';

export interface UseSecretaryResult {
  status: SecretaryStatus;
  /**
   * 稼働中のモデル名（例 'gemma4:e4b'）。未取得なら null。
   * AI秘書自身は自分のタグ(e2b/e4b)を知らないため、Gateway から取得して画面に出す。
   */
  model: string | null;
  /** 現在の会話の発言一覧。 */
  messages: ChatMessage[];
  isSending: boolean;
  error: string | null;
  send: (text: string) => Promise<void>;
  /** 全セッション。履歴一覧の表示用。 */
  sessions: ChatSession[];
  /** 現在開いている会話のID。 */
  currentId: string;
  /** 新しい会話を始める（今の会話は履歴に残る）。 */
  newSession: () => void;
  /** 過去の会話に切り替える。 */
  selectSession: (id: string) => void;
  /** 会話を削除する。現在の会話を消した場合は新しい空の会話になる。 */
  deleteSession: (id: string) => void;
}

/** 衝突しない程度に一意なセッションID。 */
function genId(): string {
  return `s${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;
}

function createSession(): ChatSession {
  return { id: genId(), title: '', updatedAt: new Date().toISOString(), messages: [] };
}

interface SessionState {
  sessions: ChatSession[];
  currentId: string;
}

/** active=true の間だけ死活監視を行う（パネルを開いている間など）。 */
export function useSecretary(active: boolean): UseSecretaryResult {
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const [status, setStatus] = useState<SecretaryStatus>('checking');
  const [model, setModel] = useState<string | null>(null);
  // 初期は空の新規会話。保存済みの履歴は Firestore から非同期で合流する
  const [state, setState] = useState<SessionState>(() => {
    const fresh = createSession();
    return { sessions: [fresh], currentId: fresh.id };
  });
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const sendingRef = useRef(false);

  const current = state.sessions.find((s) => s.id === state.currentId);
  const messages = current?.messages ?? [];

  // Firestore から履歴を読み込む（ログインユーザーが決まってから）
  useEffect(() => {
    if (!userId) return;
    let alive = true;
    void loadSessions(userId).then((loaded) => {
      if (!alive || loaded.length === 0) return;
      setState((prev) => {
        const known = new Set(prev.sessions.map((s) => s.id));
        const sessions = [...prev.sessions, ...loaded.filter((s) => !known.has(s.id))];
        // まだ何も話していなければ、前回開いていた会話の続きから始める
        const cur = prev.sessions.find((s) => s.id === prev.currentId);
        const savedId = loadCurrentId();
        const resume =
          cur?.messages.length === 0 && savedId && sessions.some((s) => s.id === savedId);
        return { sessions, currentId: resume ? savedId : prev.currentId };
      });
    });
    return () => {
      alive = false;
    };
  }, [userId]);

  // 開いている会話のIDは端末に覚えておく（リロード・再オープン時に続きから開くため）。
  // 【重要】発言のある会話のときだけ保存する。空の新規会話でIDを上書きすると、
  // 再オープン時に「前回の会話ID」が消えて復帰できず、質問ごとに別セッションへ分裂する
  // （＝履歴が「2件」ばかりに散らばる原因だった）。
  useEffect(() => {
    const cur = state.sessions.find((s) => s.id === state.currentId);
    if (cur && cur.messages.length > 0) saveCurrentId(state.currentId);
  }, [state]);

  useEffect(() => {
    if (!active) return;
    let alive = true;
    const poll = async () => {
      const health = await checkHealth();
      if (!alive) return;
      setStatus(health.up ? 'ready' : 'sleeping');
      setModel(health.model);
    };
    void poll();
    const id = window.setInterval(() => void poll(), HEALTH_POLL_MS);
    return () => {
      alive = false;
      window.clearInterval(id);
    };
  }, [active]);

  /** 現在の会話に発言を1件追加する。タイトル未設定なら最初のユーザー発言から作る。 */
  const append = useCallback((msg: ChatMessage) => {
    setState((prev) => ({
      ...prev,
      sessions: prev.sessions.map((s) =>
        s.id === prev.currentId
          ? {
              ...s,
              title: s.title || (msg.role === 'user' ? msg.content.slice(0, 24) : ''),
              updatedAt: new Date().toISOString(),
              messages: [...s.messages, msg],
            }
          : s
      ),
    }));
  }, []);

  const send = useCallback(
    async (text: string) => {
      const content = text.trim();
      if (!content || sendingRef.current) return;
      sendingRef.current = true;
      setIsSending(true);
      setError(null);
      const sessionId = state.currentId;
      const title = current?.title || content.slice(0, 24);
      // ツールが必要なら runAgent が内部で往復し、最終回答だけを返す
      const history: ChatMessage[] = [...messages, { role: 'user', content }];
      const finalMessages: ChatMessage[] = [...history];
      append({ role: 'user', content });
      try {
        // userId は書込ツールの実行者（＝ログイン本人の権限）として渡す
        const reply = await runAgent(history, userId);
        const assistantMsg: ChatMessage = {
          role: 'assistant',
          content: reply.text,
          ...(reply.writes.length > 0 ? { writes: reply.writes } : {}),
        };
        append(assistantMsg);
        finalMessages.push(assistantMsg);
      } catch (e: unknown) {
        setError(e instanceof Error ? e.message : '送信に失敗しました');
      } finally {
        sendingRef.current = false;
        setIsSending(false);
        // 保存は裏で行い、画面は待たせない（失敗してもチャットは続けられる）
        if (userId) {
          void saveSession(userId, {
            id: sessionId,
            title,
            updatedAt: new Date().toISOString(),
            messages: finalMessages,
          });
        }
      }
    },
    [messages, append, userId, state.currentId, current?.title]
  );

  const newSession = useCallback(() => {
    setError(null);
    setState((prev) => {
      const cur = prev.sessions.find((s) => s.id === prev.currentId);
      if (cur && cur.messages.length === 0) return prev; // 空の会話を量産しない
      const fresh = createSession();
      return { sessions: [fresh, ...prev.sessions], currentId: fresh.id };
    });
  }, []);

  const selectSession = useCallback((id: string) => {
    setError(null);
    setState((prev) =>
      prev.sessions.some((s) => s.id === id) ? { ...prev, currentId: id } : prev
    );
  }, []);

  const deleteSession = useCallback((id: string) => {
    void removeSession(id); // Gateway 側のローカルログからも消す（本人のログのみ。uid で分離）
    setState((prev) => {
      const rest = prev.sessions.filter((s) => s.id !== id);
      if (id !== prev.currentId) return { ...prev, sessions: rest };
      const fresh = createSession();
      return { sessions: [fresh, ...rest], currentId: fresh.id };
    });
  }, []);

  return {
    status,
    model,
    messages,
    isSending,
    error,
    send,
    sessions: state.sessions,
    currentId: state.currentId,
    newSession,
    selectSession,
    deleteSession,
  };
}
